import { Table, TableHead, TableBody } from "@mui/material";
import { differenceInMonths, format, isValid } from "date-fns";

import {
  StyledTableCellValueTemplate,
  StyledTableCellLabelTemplate,
  LabelTemplate,
  StyledTableRowTemplate,
  StyledTableContainer,
} from "../../styles/table";
import { Employee } from "../../../types/processTypes";
import { tt } from "../../../../../data/langUtility";
import { formatReferenceNumber } from "../../../utils/referenceNumber";
interface Props {
  currentEmployee: Employee;
  template: LabelTemplate;
  showHeader?: boolean | true;
  showReference?: boolean | false;
  showName?: boolean | false;
  showNationality?: boolean | true;
  showPassportNumber?: boolean | false;
  showLanguage?: boolean | true;
  showReligion?: boolean | true;
  showDateOfBirth?: boolean | true;
  showAge?: boolean | true;
  showMaritalStatus?: boolean | true;
  showNumberOfChildren?: boolean | true;
  showWeightHeight?: boolean | true;
  showComplexion?: boolean | true;
  showEducation?: boolean | false;
  showPlaceOfBirth?: boolean | false;
  showLivingTown?: boolean | false;
  showEmployeeTelephone?: boolean | false;
  showContactName?: boolean | false;
  showContactTelephone?: boolean | false;
  showYearGraduated?: boolean | false;
  showGraduatedSchool?: boolean | false;
}

type DateLike = Date | string | undefined | null;

const safeFormatDate = (value: DateLike, dateFormat: string): string => {
  if (value == null || value === "") return "";
  const d = new Date(value);
  return isValid(d) ? format(d, dateFormat) : "";
};

const safeDateOfBirthCell = (value: DateLike, cvLang: string): string => {
  if (value == null || value === "") return "";
  const d = new Date(value);
  if (!isValid(d)) return "";
  return cvLang === "en"
    ? format(d, "dd-MMM-yyyy")
    : d.toLocaleDateString("ar-SA", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      });
};

const safeAgeYears = (value: DateLike, cvLang: string): string => {
  if (value == null || value === "") return "";
  const d = new Date(value);
  if (!isValid(d)) return "";
  const years = Math.floor(differenceInMonths(new Date(), d) / 12);
  return years.toLocaleString(cvLang === "ar" ? "ar-SA" : "en-US");
};

export const BasicDetail = ({
  currentEmployee,
  template,
  showHeader,
  showReference,
  showName,
  showNationality,
  showPassportNumber,
  showReligion,
  showDateOfBirth,
  showAge,
  showMaritalStatus,
  showNumberOfChildren,
  showWeightHeight,
  showComplexion,
  showEducation,
  showPlaceOfBirth,
  showLivingTown,
  showEmployeeTelephone,
  showContactName,
  showContactTelephone,
  showYearGraduated,
  showGraduatedSchool,
}: Props) => {
  return (
    <>
      <StyledTableContainer
        template={template}
        sx={{
          marginTop: template.spacing,
        }}
      >
        <Table size="medium" aria-label="simple table">
          <TableHead>
            {showHeader && (
              <StyledTableRowTemplate template={template}>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "left",
                    width: "33%",
                  }}
                >
                  {tt("detailsOfApplication", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{ width: "34%" }}
                ></StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                    width: "33%",
                  }}
                >
                  {tt("detailsOfApplication", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
          </TableHead>
          <TableBody>
            {showReference && (
              <StyledTableRowTemplate template={template} key="0">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("referenceNumber", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {formatReferenceNumber(currentEmployee.referenceNumber)}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("referenceNumber", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showName && (
              <StyledTableRowTemplate template={template} key="434">
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "left",
                  }}
                >
                  {tt("fullName", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.givenName + " " + currentEmployee.surName}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("fullName", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showNationality && (
              <StyledTableRowTemplate template={template} key="120">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("nationality", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {tt(
                    currentEmployee.additionalData?.nationality as string,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("nationality", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showPassportNumber && (
              <StyledTableRowTemplate template={template} key="130">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("passportNo", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.passportNumber as string}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("passportNo", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showReligion && (
              <StyledTableRowTemplate template={template} key="1">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("religion", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {tt(
                    currentEmployee.additionalData?.religion as string,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("religion", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showPlaceOfBirth && (
              <StyledTableRowTemplate template={template} key="101">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("placeOfBirth", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.placeOfBirth as string}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("placeOfBirth", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showDateOfBirth && (
              <StyledTableRowTemplate template={template} key="2">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("dateOfBirth", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {safeDateOfBirthCell(
                    currentEmployee.dateOfBirth,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("dateOfBirth", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}

            {showAge && (
              <StyledTableRowTemplate template={template} key="3">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("age", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {safeAgeYears(
                    currentEmployee.dateOfBirth,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("age", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showWeightHeight && (
              <>
                <StyledTableRowTemplate template={template} key="77">
                  <StyledTableCellLabelTemplate template={template}>
                    {tt("height", "en")}
                  </StyledTableCellLabelTemplate>
                  <StyledTableCellValueTemplate template={template}>
                    {currentEmployee.additionalData?.height === ""
                      ? "-"
                      : currentEmployee.additionalData?.height +
                        tt("meter", template.cvLang)}
                  </StyledTableCellValueTemplate>
                  <StyledTableCellLabelTemplate
                    template={template}
                    sx={{
                      textAlign: "right",
                    }}
                  >
                    {tt("height", "ar")}
                  </StyledTableCellLabelTemplate>
                </StyledTableRowTemplate>
                <StyledTableRowTemplate template={template} key="7">
                  <StyledTableCellLabelTemplate template={template}>
                    {tt("weight", "en")}
                  </StyledTableCellLabelTemplate>
                  <StyledTableCellValueTemplate template={template}>
                    {currentEmployee.additionalData?.weight === ""
                      ? "-"
                      : currentEmployee.additionalData?.weight +
                        tt("kg", template.cvLang)}
                  </StyledTableCellValueTemplate>
                  <StyledTableCellLabelTemplate
                    template={template}
                    sx={{
                      textAlign: "right",
                    }}
                  >
                    {tt("weight", "ar")}
                  </StyledTableCellLabelTemplate>
                </StyledTableRowTemplate>
              </>
            )}
            {showComplexion && (
              <StyledTableRowTemplate template={template} key="9">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("complexion", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {"LTB"}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("complexion", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showMaritalStatus && (
              <StyledTableRowTemplate template={template} key="5">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("maritalStatus", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {tt(
                    currentEmployee.additionalData?.maritalStatus as string,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("maritalStatus", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showNumberOfChildren && (
              <StyledTableRowTemplate template={template} key="6">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("numberOfChildren", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.additionalData?.numberOfChildren?.toLocaleString(
                    template.cvLang === "ar" ? "ar-SA" : "en-US",
                  )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("numberOfChildren", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}

            {showEducation && (
              <StyledTableRowTemplate template={template} key="10">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("education_level", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {tt(
                    currentEmployee.education?.educationLevel as string,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("education_level", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showLivingTown && (
              <StyledTableRowTemplate template={template} key="11">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("living_town", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.placeOfBirth as string}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("living_town", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showEmployeeTelephone && (
              <StyledTableRowTemplate template={template} key="12">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("employee_telephone", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.address
                    ? (currentEmployee.address?.telephone as string)
                    : ""}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("employee_telephone", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showContactName && (
              <StyledTableRowTemplate template={template} key="13">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("contact_name", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.contactPerson
                    ? (currentEmployee.contactPerson?.displayName as string)
                    : ""}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("contact_name", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showContactTelephone && (
              <StyledTableRowTemplate template={template} key="14">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("contact_telephone", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.contactPerson &&
                  currentEmployee.contactPerson.address
                    ? (currentEmployee.contactPerson.address
                        ?.telephone as string)
                    : ""}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("contact_telephone", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showYearGraduated && (
              <StyledTableRowTemplate template={template} key="141">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("year_graduated", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  2023
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("year_graduated", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showGraduatedSchool && (
              <StyledTableRowTemplate template={template} key="142">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("graduated_school", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  COC center
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("graduated_school", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </>
  );
};
export const BasicDetailGolden = ({
  currentEmployee,
  template,
  showHeader,
  showReference,
  showNationality,
  showPassportNumber,
  showLanguage,
  showReligion,
  showDateOfBirth,
  showAge,
  showMaritalStatus,
  showNumberOfChildren,
  showWeightHeight,
  showComplexion,
  showEducation,
  showPlaceOfBirth,
  showLivingTown,
  showEmployeeTelephone,
  showContactName,
  showContactTelephone,
  showYearGraduated,
  showGraduatedSchool,
}: Props) => {
  return (
    <>
      <StyledTableContainer
        template={template}
        sx={{
          marginTop: template.spacing,
        }}
      >
        <Table size="medium" aria-label="simple table">
          <TableHead>
            {showHeader && (
              <StyledTableRowTemplate template={template}>
                <StyledTableCellLabelTemplate
                  template={template}
                  colSpan={3}
                  sx={{
                    textAlign: "center",
                  }}
                >
                  {tt("detailsOfApplication", "en")}{" "}
                  {tt("detailsOfApplication", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
          </TableHead>
          <TableBody>
            {showReference && (
              <StyledTableRowTemplate template={template} key="0">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("referenceNumber", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("referenceNumber", "ar")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {formatReferenceNumber(currentEmployee.referenceNumber)}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showNationality && (
              <StyledTableRowTemplate template={template} key="120">
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    width: "30%",
                  }}
                >
                  {tt("nationality", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{
                    textAlign: "center",
                  }}
                >
                  {tt(
                    currentEmployee.additionalData?.nationality as string,
                    "en",
                  ) +
                    "-" +
                    tt(
                      currentEmployee.additionalData?.nationality as string,
                      "ar",
                    )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                    width: "30%",
                  }}
                >
                  {tt("nationality", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showPassportNumber && (
              <StyledTableRowTemplate template={template} key="130">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("passportNo", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("passportNo", "ar")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.passportNumber as string}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}

            {showDateOfBirth && (
              <StyledTableRowTemplate template={template} key="2">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("dateOfBirth", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{
                    textAlign: "center",
                  }}
                >
                  {safeDateOfBirthCell(
                    currentEmployee.dateOfBirth,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("dateOfBirth", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showPlaceOfBirth && (
              <StyledTableRowTemplate template={template} key="101">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("placeOfBirth", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{
                    textAlign: "center",
                  }}
                >
                  {tt(
                    currentEmployee.additionalData?.nationality as string,
                    "en",
                  ) +
                    "-" +
                    tt(
                      currentEmployee.additionalData?.nationality as string,
                      "ar",
                    )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("placeOfBirth", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showAge && (
              <StyledTableRowTemplate template={template} key="3">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("age", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("age", "ar")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {safeAgeYears(
                    currentEmployee.dateOfBirth,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showMaritalStatus && (
              <StyledTableRowTemplate template={template} key="5">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("maritalStatus", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{
                    textAlign: "center",
                  }}
                >
                  {tt(
                    currentEmployee.additionalData?.maritalStatus as string,
                    "en",
                  ) +
                    "-" +
                    tt(
                      currentEmployee.additionalData?.maritalStatus as string,
                      "ar",
                    )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("maritalStatus", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showNumberOfChildren && (
              <StyledTableRowTemplate template={template} key="6">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("numberOfChildren", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{
                    textAlign: "center",
                  }}
                >
                  {currentEmployee.additionalData?.numberOfChildren?.toLocaleString(
                    template.cvLang === "ar" ? "ar-SA" : "en-US",
                  )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("numberOfChildren", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            <StyledTableRowTemplate template={template} key="62">
              <StyledTableCellLabelTemplate template={template}>
                {"Look"}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate
                template={template}
                sx={{
                  textAlign: "center",
                }}
              >
                {"Accepted - مقبول"}
              </StyledTableCellValueTemplate>
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {"المظهر"}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
            {showReligion && (
              <StyledTableRowTemplate template={template} key="15">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("religion", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{
                    textAlign: "center",
                  }}
                >
                  {tt(
                    currentEmployee.additionalData?.religion as string,
                    "en",
                  ) +
                    "-" +
                    tt(
                      currentEmployee.additionalData?.religion as string,
                      "ar",
                    )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("religion", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            <StyledTableRowTemplate template={template} key="65">
              <StyledTableCellLabelTemplate template={template}>
                {"Language"}
              </StyledTableCellLabelTemplate>
              <StyledTableCellValueTemplate
                template={template}
                sx={{
                  textAlign: "center",
                }}
              >
                {tt(
                  currentEmployee.additionalData?.nationality as string,
                  "en",
                ) +
                  "-" +
                  tt(
                    currentEmployee.additionalData?.nationality as string,
                    "ar",
                  )}
              </StyledTableCellValueTemplate>
              <StyledTableCellLabelTemplate
                template={template}
                sx={{
                  textAlign: "right",
                }}
              >
                {tt("language", "ar")}
              </StyledTableCellLabelTemplate>
            </StyledTableRowTemplate>
            {showWeightHeight && (
              <>
                <StyledTableRowTemplate template={template} key="77">
                  <StyledTableCellLabelTemplate template={template}>
                    {tt("height", "en")}
                  </StyledTableCellLabelTemplate>
                  <StyledTableCellValueTemplate
                    template={template}
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    {currentEmployee.additionalData?.height === ""
                      ? "-"
                      : currentEmployee.additionalData?.height +
                        tt("meter", template.cvLang)}
                  </StyledTableCellValueTemplate>
                  <StyledTableCellLabelTemplate
                    template={template}
                    sx={{
                      textAlign: "right",
                    }}
                  >
                    {tt("height", "ar")}
                  </StyledTableCellLabelTemplate>
                </StyledTableRowTemplate>
                <StyledTableRowTemplate template={template} key="7">
                  <StyledTableCellLabelTemplate template={template}>
                    {tt("weight", "en")}
                  </StyledTableCellLabelTemplate>
                  <StyledTableCellValueTemplate
                    template={template}
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    {currentEmployee.additionalData?.weight === ""
                      ? "-"
                      : currentEmployee.additionalData?.weight +
                        tt("kg", template.cvLang)}
                  </StyledTableCellValueTemplate>
                  <StyledTableCellLabelTemplate
                    template={template}
                    sx={{
                      textAlign: "right",
                    }}
                  >
                    {tt("weight", "ar")}
                  </StyledTableCellLabelTemplate>
                </StyledTableRowTemplate>
              </>
            )}
            {showComplexion && (
              <StyledTableRowTemplate template={template} key="9">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("complexion", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("complexion", "ar")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{
                    textAlign: "center",
                  }}
                >
                  {"LTB"}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showEducation && (
              <StyledTableRowTemplate template={template} key="10">
                <StyledTableCellLabelTemplate template={template}>
                  {"Qualifications"}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{
                    textAlign: "center",
                  }}
                >
                  {tt(
                    currentEmployee.education?.educationLevel as string,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {"المؤهالت"}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showLivingTown && (
              <StyledTableRowTemplate template={template} key="11">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("living_town", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("living_town", "ar")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.address
                    ? (currentEmployee.address?.city as string)
                    : ""}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showEmployeeTelephone && (
              <StyledTableRowTemplate template={template} key="12">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("employee_telephone", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("employee_telephone", "ar")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.address
                    ? (currentEmployee.address?.telephone as string)
                    : ""}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showContactName && (
              <StyledTableRowTemplate template={template} key="13">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("contact_name", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("contact_name", "ar")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.contactPerson
                    ? (currentEmployee.contactPerson?.displayName as string)
                    : ""}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showContactTelephone && (
              <StyledTableRowTemplate template={template} key="14">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("contact_telephone", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("contact_telephone", "ar")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.contactPerson &&
                  currentEmployee.contactPerson.address
                    ? (currentEmployee.contactPerson.address
                        ?.telephone as string)
                    : ""}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showYearGraduated && (
              <StyledTableRowTemplate template={template} key="141">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("year_graduated", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("year_graduated", "ar")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  2023
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showGraduatedSchool && (
              <StyledTableRowTemplate template={template} key="142">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("graduated_school", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("graduated_school", "ar")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  COC center
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showLanguage && (
              <>
                <StyledTableRowTemplate template={template} key="3408">
                  <StyledTableCellLabelTemplate template={template}>
                    {tt("arabic", "en")}
                  </StyledTableCellLabelTemplate>
                  <StyledTableCellLabelTemplate
                    template={template}
                    sx={{
                      textAlign: "right",
                    }}
                  >
                    {tt("arabic", "ar")}
                  </StyledTableCellLabelTemplate>
                  <StyledTableCellValueTemplate template={template}>
                    {tt(
                      currentEmployee.education?.arabicLanguage as string,
                      template.cvLang,
                    )}
                  </StyledTableCellValueTemplate>
                </StyledTableRowTemplate>
                <StyledTableRowTemplate template={template} key="3411">
                  <StyledTableCellLabelTemplate template={template}>
                    {tt("english", "en")}
                  </StyledTableCellLabelTemplate>
                  <StyledTableCellLabelTemplate
                    template={template}
                    sx={{
                      textAlign: "right",
                    }}
                  >
                    {tt("english", "ar")}
                  </StyledTableCellLabelTemplate>
                  <StyledTableCellValueTemplate template={template}>
                    {tt(
                      currentEmployee.education?.englishLanguage as string,
                      template.cvLang,
                    )}
                  </StyledTableCellValueTemplate>
                </StyledTableRowTemplate>
              </>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </>
  );
};

export const BasicDetailNoLabels = ({
  currentEmployee,
  template,
  showHeader,
  showReference,
  showNationality,
  showPassportNumber,
  showReligion,
  showDateOfBirth,
  showAge,
  showMaritalStatus,
  showNumberOfChildren,
  showWeightHeight,
  showComplexion,
  showEducation,
  showPlaceOfBirth,
  showLivingTown,
  showEmployeeTelephone,
  showContactName,
  showContactTelephone,
  showYearGraduated,
  showGraduatedSchool,
}: Props) => {
  return (
    <>
      <StyledTableContainer
        template={template}
        sx={{
          marginTop: template.spacing,
        }}
      >
        <Table size="medium" aria-label="simple table">
          <TableHead>
            {showHeader && (
              <StyledTableRowTemplate template={template}>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "left",
                    width: "33%",
                  }}
                >
                  {tt("detailsOfApplication", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{ width: "34%" }}
                ></StyledTableCellLabelTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                    width: "33%",
                  }}
                >
                  {tt("detailsOfApplication", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
          </TableHead>
          <TableBody>
            {showReference && (
              <StyledTableRowTemplate template={template} key="0">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("referenceNumber", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {formatReferenceNumber(currentEmployee.referenceNumber)}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("referenceNumber", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showNationality && (
              <StyledTableRowTemplate template={template} key="120">
                <StyledTableCellValueTemplate
                  colSpan={3}
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {tt(
                    currentEmployee.additionalData?.nationality as string,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showPassportNumber && (
              <StyledTableRowTemplate template={template} key="130">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("passportNo", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {currentEmployee.passportNumber as string}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("passportNo", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showReligion && (
              <StyledTableRowTemplate template={template} key="1">
                <StyledTableCellValueTemplate
                  colSpan={3}
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {tt(
                    currentEmployee.additionalData?.religion as string,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showDateOfBirth && (
              <StyledTableRowTemplate template={template} key="2">
                <StyledTableCellValueTemplate
                  colSpan={3}
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {safeDateOfBirthCell(
                    currentEmployee.dateOfBirth,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showAge && (
              <StyledTableRowTemplate template={template} key="3">
                <StyledTableCellValueTemplate
                  colSpan={3}
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {safeAgeYears(
                    currentEmployee.dateOfBirth,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showEmployeeTelephone && (
              <StyledTableRowTemplate template={template} key="12">
                <StyledTableCellValueTemplate
                  colSpan={3}
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {currentEmployee.address
                    ? (currentEmployee.address?.telephone as string)
                    : ""}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showNumberOfChildren && (
              <StyledTableRowTemplate template={template} key="6">
                <StyledTableCellValueTemplate
                  colSpan={3}
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {currentEmployee.additionalData?.numberOfChildren?.toLocaleString(
                    template.cvLang === "ar" ? "ar-SA" : "en-US",
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showMaritalStatus && (
              <StyledTableRowTemplate template={template} key="5">
                <StyledTableCellValueTemplate
                  template={template}
                  colSpan={3}
                  sx={{ textAlign: "center" }}
                >
                  {tt(
                    currentEmployee.additionalData?.maritalStatus as string,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showEducation && (
              <StyledTableRowTemplate template={template} key="10">
                <StyledTableCellValueTemplate
                  colSpan={3}
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {tt(
                    currentEmployee.education?.educationLevel as string,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showPlaceOfBirth && (
              <StyledTableRowTemplate template={template} key="101">
                <StyledTableCellValueTemplate
                  colSpan={3}
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {currentEmployee.placeOfBirth as string}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showPlaceOfBirth && (
              <StyledTableRowTemplate template={template} key="1011">
                <StyledTableCellValueTemplate
                  colSpan={3}
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {currentEmployee.placeOfBirth as string}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showWeightHeight && (
              <>
                <StyledTableRowTemplate template={template} key="7">
                  <StyledTableCellValueTemplate
                    template={template}
                    colSpan={3}
                    sx={{ textAlign: "center" }}
                  >
                    {currentEmployee.additionalData?.weight === ""
                      ? "-"
                      : currentEmployee.additionalData?.weight +
                        tt("kg", template.cvLang)}
                  </StyledTableCellValueTemplate>
                </StyledTableRowTemplate>

                <StyledTableRowTemplate template={template} key="77">
                  <StyledTableCellValueTemplate
                    template={template}
                    colSpan={3}
                    sx={{ textAlign: "center" }}
                  >
                    {currentEmployee.additionalData?.height === ""
                      ? "-"
                      : currentEmployee.additionalData?.height +
                        tt("meter", template.cvLang)}
                  </StyledTableCellValueTemplate>
                </StyledTableRowTemplate>
              </>
            )}
            {showWeightHeight && (
              <StyledTableRowTemplate template={template} key="908">
                <StyledTableCellValueTemplate
                  template={template}
                  colSpan={3}
                  sx={{ textAlign: "center", pl: 6 }}
                >
                  {currentEmployee.workExperience &&
                  currentEmployee.workExperience[0] ? (
                    currentEmployee.workExperience.map((row, index) => (
                      <>
                        {tt(row.country as string, template.cvLang) +
                          "-" +
                          tt(row.years as string, template.cvLang)}
                        <br />
                      </>
                    ))
                  ) : (
                    <>{tt("firstTimer", template.cvLang)}</>
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showComplexion && (
              <StyledTableRowTemplate template={template} key="9">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("complexion", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {"LTB"}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("complexion", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showLivingTown && (
              <StyledTableRowTemplate template={template} key="11">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("living_town", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {currentEmployee.address
                    ? (currentEmployee.address?.city as string)
                    : ""}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("living_town", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showContactName && (
              <StyledTableRowTemplate template={template} key="13">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("contact_name", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {currentEmployee.contactPerson
                    ? (currentEmployee.contactPerson?.displayName as string)
                    : ""}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("contact_name", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showContactTelephone && (
              <StyledTableRowTemplate template={template} key="14">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("contact_telephone", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  {currentEmployee.contactPerson &&
                  currentEmployee.contactPerson.address
                    ? (currentEmployee.contactPerson.address
                        ?.telephone as string)
                    : ""}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("contact_telephone", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showYearGraduated && (
              <StyledTableRowTemplate template={template} key="141">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("year_graduated", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  2023
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("year_graduated", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showGraduatedSchool && (
              <StyledTableRowTemplate template={template} key="142">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("graduated_school", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{ textAlign: "center" }}
                >
                  COC center
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {tt("graduated_school", "ar")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </>
  );
};
interface PropsEnglish {
  currentEmployee: Employee;
  template: LabelTemplate;
  showHeader?: boolean | true;
  showName?: boolean | false;
  showReference?: boolean | false;
  showProfession?: boolean | false;
  showSalary?: boolean | false;
  showContractPeriod?: boolean | false;
  showPassportNumber?: boolean | false;
  showIssueDate?: boolean | false;
  showPlaceOfIssue?: boolean | false;
  showExpiryDate?: boolean | false;
  showLanguage?: boolean | true;
  showNationality?: boolean | true;
  showReligion?: boolean | true;
  showDateOfBirth?: boolean | true;
  showAge?: boolean | true;
  showMaritalStatus?: boolean | true;
  showNumberOfChildren?: boolean | true;
  showWeightHeight?: boolean | true;
  showComplexion?: boolean | true;
  showEducation?: boolean | false;
  showPlaceOfBirth?: boolean | false;
  showLivingTown?: boolean | false;
  showEmployeeTelephone?: boolean | false;
  showContactName?: boolean | false;
  showContactTelephone?: boolean | false;
  showYearGraduated?: boolean | false;
  showGraduatedSchool?: boolean | false;
}
export const BasicDetailEnglish = ({
  currentEmployee,
  template,
  showHeader,
  showName,
  showReference,
  showProfession,
  showSalary,
  showContractPeriod,
  showPassportNumber,
  showIssueDate,
  showPlaceOfIssue,
  showExpiryDate,
  showLanguage,
  showNationality,
  showReligion,
  showDateOfBirth,
  showAge,
  showMaritalStatus,
  showNumberOfChildren,
  showWeightHeight,
  showComplexion,
  showEducation,
  showPlaceOfBirth,
  showLivingTown,
  showEmployeeTelephone,
  showContactName,
  showContactTelephone,
  showYearGraduated,
  showGraduatedSchool,
}: PropsEnglish) => {
  return (
    <>
      <StyledTableContainer
        template={template}
        sx={{
          marginTop: template.spacing,
        }}
      >
        <Table size="medium" aria-label="simple table">
          <TableHead>
            {showHeader && (
              <StyledTableRowTemplate template={template}>
                <StyledTableCellLabelTemplate
                  template={template}
                  colSpan={2}
                  sx={{
                    textAlign: "center",
                  }}
                >
                  {tt("detailsOfApplication", "en")}
                </StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
          </TableHead>
          <TableBody>
            {showName && (
              <StyledTableRowTemplate template={template} key="0">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("name", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.givenName + " " + currentEmployee.surName}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showReference && (
              <StyledTableRowTemplate template={template} key="0">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("referenceNumber", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {formatReferenceNumber(currentEmployee.referenceNumber)}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showProfession && (
              <StyledTableRowTemplate template={template} key="3">
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "left",
                    width: "25%",
                  }}
                >
                  {tt("profession", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate
                  template={template}
                  sx={{
                    textAlign: "left",
                    width: "50%",
                  }}
                >
                  {tt(
                    currentEmployee.additionalData?.appliedProfession as string,
                    "en",
                  )
                    .toLowerCase()
                    .toUpperCase()}
                </StyledTableCellValueTemplate>
                <StyledTableCellLabelTemplate
                  template={template}
                  sx={{
                    textAlign: "left",
                    width: "25%",
                  }}
                ></StyledTableCellLabelTemplate>
              </StyledTableRowTemplate>
            )}
            {showSalary && (
              <StyledTableRowTemplate template={template} key="2">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("salary", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.additionalData?.salary?.toLocaleString(
                    "en-US",
                  ) +
                    " " +
                    tt(
                      (
                        currentEmployee.additionalData?.currency ?? ""
                      ).toLowerCase(),
                      "en",
                    )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showEmployeeTelephone && (
              <StyledTableRowTemplate template={template} key="12">
                <StyledTableCellLabelTemplate template={template}>
                  {"Contact Number"}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.address
                    ? (currentEmployee.address?.telephone as string)
                    : ""}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showEducation && (
              <StyledTableRowTemplate template={template} key="10">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("education_level", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {tt(
                    currentEmployee.education?.educationLevel as string,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showContractPeriod && (
              <StyledTableRowTemplate template={template} key="1">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("contractPeriod", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {tt(
                    currentEmployee.additionalData?.contractPeriod as string,
                    "en",
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showPassportNumber && (
              <StyledTableRowTemplate template={template} key="0">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("passportNo", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.passportNumber as string}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showIssueDate && (
              <StyledTableRowTemplate template={template} key="1">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("dateOfIssue", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {safeFormatDate(
                    currentEmployee.passportIssueDate,
                    "dd-MMM-yyyy",
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showPlaceOfIssue && (
              <StyledTableRowTemplate template={template} key="2">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("placeOfIssue", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.placeOfIssue as string}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showExpiryDate && (
              <StyledTableRowTemplate template={template} key="3">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("dateOfExpiry", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {safeFormatDate(
                    currentEmployee.passportExpiryDate,
                    "dd-MMM-yyyy",
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showLanguage && (
              <>
                <StyledTableRowTemplate template={template} key="340">
                  <StyledTableCellLabelTemplate template={template}>
                    {"Arabic Level"}
                  </StyledTableCellLabelTemplate>
                  <StyledTableCellValueTemplate template={template}>
                    {tt(
                      currentEmployee.education?.arabicLanguage as string,
                      template.cvLang,
                    )}
                  </StyledTableCellValueTemplate>
                </StyledTableRowTemplate>
                <StyledTableRowTemplate template={template} key="3411">
                  <StyledTableCellLabelTemplate template={template}>
                    {"English Level"}
                  </StyledTableCellLabelTemplate>
                  <StyledTableCellValueTemplate template={template}>
                    {tt(
                      currentEmployee.education?.englishLanguage as string,
                      template.cvLang,
                    )}
                  </StyledTableCellValueTemplate>
                </StyledTableRowTemplate>
              </>
            )}
            {showNationality && (
              <StyledTableRowTemplate template={template} key="120">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("nationality", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {tt(
                    currentEmployee.additionalData?.nationality as string,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showPassportNumber && (
              <StyledTableRowTemplate template={template} key="130">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("telephone", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.address?.telephone as string}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showReligion && (
              <StyledTableRowTemplate template={template} key="1">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("religion", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {tt(
                    currentEmployee.additionalData?.religion as string,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showDateOfBirth && (
              <StyledTableRowTemplate template={template} key="2">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("dateOfBirth", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {safeFormatDate(
                    currentEmployee.dateOfBirth,
                    "dd-MMM-yyyy",
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}

            {showAge && (
              <StyledTableRowTemplate template={template} key="3">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("age", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {safeAgeYears(
                    currentEmployee.dateOfBirth,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showMaritalStatus && (
              <StyledTableRowTemplate template={template} key="5">
                <StyledTableCellLabelTemplate template={template}>
                  {"Civil Status"}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {tt(
                    currentEmployee.additionalData?.maritalStatus as string,
                    template.cvLang,
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showNumberOfChildren && (
              <StyledTableRowTemplate template={template} key="6">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("numberOfChildren", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.additionalData?.numberOfChildren?.toLocaleString(
                    "en-US",
                  )}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showWeightHeight && (
              <>
                <StyledTableRowTemplate template={template} key="7">
                  <StyledTableCellLabelTemplate template={template}>
                    {tt("weight", "en")}
                  </StyledTableCellLabelTemplate>
                  <StyledTableCellValueTemplate template={template}>
                    {currentEmployee.additionalData?.weight === ""
                      ? "-"
                      : currentEmployee.additionalData?.weight +
                        tt("kg", template.cvLang)}
                  </StyledTableCellValueTemplate>
                </StyledTableRowTemplate>

                <StyledTableRowTemplate template={template} key="77">
                  <StyledTableCellLabelTemplate template={template}>
                    {tt("height", "en")}
                  </StyledTableCellLabelTemplate>
                  <StyledTableCellValueTemplate template={template}>
                    {currentEmployee.additionalData?.height === ""
                      ? "-"
                      : currentEmployee.additionalData?.height +
                        tt("meter", template.cvLang)}
                  </StyledTableCellValueTemplate>
                </StyledTableRowTemplate>
              </>
            )}
            {showComplexion && (
              <StyledTableRowTemplate template={template} key="9">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("complexion", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {"LTB"}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}

            {showPlaceOfBirth && (
              <StyledTableRowTemplate template={template} key="101">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("placeOfBirth", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.placeOfBirth as string}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showLivingTown && (
              <StyledTableRowTemplate template={template} key="11">
                <StyledTableCellLabelTemplate template={template}>
                  {"Address"}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.address
                    ? (currentEmployee.address?.city as string)
                    : ""}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}

            {showContactName && (
              <StyledTableRowTemplate template={template} key="13">
                <StyledTableCellLabelTemplate template={template}>
                  {"EmergencyContact Name"}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.contactPerson
                    ? (currentEmployee.contactPerson?.displayName as string)
                    : ""}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showContactTelephone && (
              <StyledTableRowTemplate template={template} key="14">
                <StyledTableCellLabelTemplate template={template}>
                  {"Emergency Contact Telephone"}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  {currentEmployee.contactPerson &&
                  currentEmployee.contactPerson.address
                    ? (currentEmployee.contactPerson.address
                        ?.telephone as string)
                    : ""}
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showYearGraduated && (
              <StyledTableRowTemplate template={template} key="141">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("year_graduated", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  2023
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
            {showGraduatedSchool && (
              <StyledTableRowTemplate template={template} key="142">
                <StyledTableCellLabelTemplate template={template}>
                  {tt("graduated_school", "en")}
                </StyledTableCellLabelTemplate>
                <StyledTableCellValueTemplate template={template}>
                  COC center
                </StyledTableCellValueTemplate>
              </StyledTableRowTemplate>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </>
  );
};
